import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription, timer } from 'rxjs';

import { Company } from '../../../../../models/company.model';
import { Negotiation } from '../../models/negotiation.model';
import { ConfirmaStatus, NegotiationService } from '../../services/negotiation.service';

/** Códigos BCR de estado del contrato. */
const BCR_PENDING_CONTROL = [1, 2];
const BCR_IN_SIGNATURE = 3;
const BCR_PENDING_REGISTRATION = 4;
const BCR_REGISTERED = 5;
/** Anulado, excluido por tiempo excedido y anulado post-registración. */
const BCR_VOIDED = [7, 9, 99];
/** Excluido por tiempo excedido: venció el plazo para firmar en Confirma. */
const BCR_EXPIRED = 9;
/** Negociación cerrada (anotada). */
const NEGOTIATION_CLOSED = 7;
/** Polling: una consulta cada 15 s, hasta 5 minutos. */
const POLLING_INTERVAL_MS = 15000;
const POLLING_MAX_ATTEMPTS = 20;
/** Respuestas del reintento que indican que no corresponde reintentar (no son fallas). */
const RETRY_REJECTED_STATUSES = [409, 422];
/** Estado del alert según la severidad que informa api2. */
const SEVERITY_ALERT: { [severity: string]: string } = {
  danger: 'danger',
  warning: 'warning',
  success: 'success',
  info: 'info',
  muted: 'info'
};

/** Qué texto muestra el cartel. */
export type ConfirmaView = 'retry_sent' | 'voided' | 'blocked' | 'processing' |
  'pending_control' | 'in_signature' | 'pending_registration' | 'registered' | 'in_progress';

/**
 * Estado del contrato de una negociación ODD en Confirma (Bolsa de Comercio de
 * Rosario). api2 decide si aplica, si está bloqueado y si se puede reintentar
 * (`applies`, `blocked`, `retryable`); acá sólo se elige el texto. El cartel se
 * muestra mientras el sobre CONFIRMA no esté completado; una vez completado queda
 * una sola línea con el N° BCR (el PDF se descarga con "Descargar contrato").
 *
 * ## Usage
 * ``` html
 * <ag-negotiation-confirma [company]="company" [negotiation]="negotiation"></ag-negotiation-confirma>
 * ```
 */
@Component({
  selector: 'ag-negotiation-confirma',
  templateUrl: './negotiation-confirma.component.html'
})
export class NegotiationConfirmaComponent implements OnChanges, OnDestroy {

  @Input() public company: Company;
  @Input() public negotiation: Negotiation;

  public status: ConfirmaStatus;
  public processing: boolean;
  public retrySent: boolean;
  public retryError: boolean;
  /** Hay una consulta de estado programada. */
  public polling: boolean;

  /** Negociación (id + estado) para la que se cargó el estado. */
  private loadedKey: string;
  private pollingAttempt: number = 0;
  private pollingSubscription: Subscription;
  private subscriptions: Subscription[] = [];

  constructor(
    private negotiationService: NegotiationService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.negotiation && !changes.company) return;

    const key = this.negotiation?.id && this.company?.id ?
      [this.company.id, this.negotiation.id, this.negotiation.status?.id].join(':') : undefined;
    if (key === this.loadedKey) return;

    this.reset();
    this.loadedKey = key;
    if (key && this.shouldLoad) this.load();
  }

  /**
   * Negocio ODD que se anotó alguna vez (cerrado o modificado después). Sólo
   * evita consultar negociaciones que nunca llegaron a la BCR; qué se muestra lo
   * decide `applies` de api2.
   */
  private get shouldLoad(): boolean {
    const negotiation = this.negotiation;
    const businessType = negotiation?.proposal?.business_detail?.business_type ||
      negotiation?.order?.business_detail?.business_type;
    const isOdd = typeof businessType === 'string' && businessType.toLowerCase() === 'odd';
    return isOdd && (negotiation.status?.id === NEGOTIATION_CLOSED || !!negotiation.booking_date ||
      !!negotiation.original_booking_date || negotiation.times_booked > 0);
  }

  get visible(): boolean {
    return !!this.status?.applies;
  }

  private get code(): number {
    return this.status?.document?.status?.code;
  }

  get registered(): boolean {
    return this.code === BCR_REGISTERED;
  }

  /** Anulado, excluido por tiempo o anulado post-registración en la BCR. */
  get voided(): boolean {
    return BCR_VOIDED.includes(this.code);
  }

  /** Excluido por tiempo excedido (venció el plazo para firmar). */
  get expired(): boolean {
    return this.code === BCR_EXPIRED;
  }

  /** El sobre CONFIRMA está completado: sólo queda la línea con el N° BCR. */
  get completed(): boolean {
    return !!this.status?.envelope_completed;
  }

  /** Texto a mostrar según el estado (el orden define la prioridad). */
  get view(): ConfirmaView {
    const document = this.status?.document;
    if (this.retrySent) return 'retry_sent';
    if (this.voided) return 'voided';
    // Sin documento justo después de anotar (el alta es asincrónica): "procesando"
    // mientras se siga consultando.
    if (!document && this.polling) return 'processing';
    if (this.status?.blocked) return 'blocked';
    if (!document?.confirma_id) return 'processing';
    if (BCR_PENDING_CONTROL.includes(this.code)) return 'pending_control';
    if (this.code === BCR_IN_SIGNATURE) return 'in_signature';
    if (this.code === BCR_PENDING_REGISTRATION) return 'pending_registration';
    if (this.registered) return 'registered';
    return 'in_progress';
  }

  /** Tipo de alert: baja en la BCR, bloqueo o la severidad del estado BCR. */
  get alertType(): string {
    const severity = SEVERITY_ALERT[this.status?.document?.status?.severity];
    const view = this.view;
    if (view === 'voided') return 'danger';
    if (view === 'processing') return 'info';
    if (view === 'blocked') return severity === 'danger' ? 'danger' : 'warning';
    return severity || (this.registered ? 'success' : 'info');
  }

  /** Mensaje del catálogo de agrology-confirma (nunca el motivo técnico crudo). */
  get reason(): string {
    const mensaje = this.status?.document?.motivo?.mensaje;
    return typeof mensaje === 'string' && mensaje.trim() ? mensaje.trim() : undefined;
  }

  get showRetry(): boolean {
    return !!this.status?.retryable && this.view === 'blocked' && !this.completed;
  }

  public retry(): void {
    this.processing = true;
    this.retryError = false;
    this.track(this.negotiationService.confirmaRetry(this.company.id, this.negotiation.id).subscribe({
      next: () => {
        this.processing = false;
        this.retrySent = true;
        this.pollingAttempt = 0;
        this.schedule();
      },
      error: (error: HttpErrorResponse) => {
        this.processing = false;
        if (RETRY_REJECTED_STATUSES.includes(error?.status)) {
          // No reintentable o sin datos: se refresca el estado (retryable:false oculta el botón).
          this.load();
        } else {
          // 5xx o error de red.
          this.retryError = true;
        }
      }
    }));
  }

  private load(): void {
    this.track(this.negotiationService.confirmaStatus(this.company.id, this.negotiation.id).subscribe({
      next: status => {
        this.status = status;
        // El reintento tuvo efecto cuando api2 deja de informar el bloqueo.
        if (!status?.blocked) this.retrySent = false;
        this.schedule();
      },
      error: () => {
        // Error transitorio: se mantiene el estado anterior y se sigue consultando;
        // sin estado previo no se muestra nada (el sobre sigue en "Sobres relacionados").
        if (this.status) this.schedule();
        else this.stopPolling();
      }
    }));
  }

  /** Guarda la suscripción descartando las ya terminadas, para que el array no crezca con el polling. */
  private track(subscription: Subscription): void {
    this.subscriptions = this.subscriptions.filter(s => !s.closed);
    this.subscriptions.push(subscription);
  }

  /**
   * Se consulta mientras falte el documento o el N° BCR, tras reintentar, o
   * registrado a la espera del PDF. Nunca con el sobre completado, dado de baja
   * o bloqueado (salvo tras reintentar).
   */
  private get shouldPoll(): boolean {
    const document = this.status?.document;
    if (!this.status?.applies || this.completed || this.voided) return false;
    if (this.retrySent || !document) return true;
    if (this.status.blocked) return false;
    return !document.confirma_id || this.registered;
  }

  /** Programa la próxima consulta, o termina el polling al agotar los intentos. */
  private schedule(): void {
    this.pollingSubscription?.unsubscribe();
    if (!this.shouldPoll || this.pollingAttempt >= POLLING_MAX_ATTEMPTS) {
      this.stopPolling();
      return;
    }
    this.pollingAttempt++;
    this.polling = true;
    this.pollingSubscription = timer(POLLING_INTERVAL_MS).subscribe(() => this.load());
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
    this.polling = false;
    // Si tras consultar sigue igual, se vuelve a ofrecer el reintento.
    this.retrySent = false;
  }

  private reset(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
    this.stopPolling();
    this.status = undefined;
    this.processing = false;
    this.retryError = false;
    this.pollingAttempt = 0;
  }

  ngOnDestroy(): void {
    this.reset();
  }
}
